// ============================================================================
// refreshLock — ONE refresh-token rotation at a time, ACROSS TABS
// ============================================================================
//
// SESSION-LOGOUT-WHILE-POLLING-01 / H2.
//
// lib/api.ts already has a single-flight (`isRefreshing` + `refreshQueue`), but
// it is a module-level variable and therefore per JS context: one per TAB. The
// tokens it guards are NOT per tab -- they live in localStorage, shared by every
// tab on the origin -- and the backend rotates them SINGLE-USE with no grace
// window (refresh_token_service.rotate_refresh_token revokes the old row and
// commits before minting the new one).
//
// So two tabs whose silent-refresh timers were armed off the same `exp` fire
// within milliseconds of each other, both read RT1, and both POST it:
//
//   tab A  ──POST RT1──>  200, RT1 revoked, RT2 issued
//   tab B  ──POST RT1──>  401 "Invalid or expired refresh token"
//
// Tab B's 401 reached forceLogoutToLoginPage(), which clears the shared storage
// and broadcasts LOGGED_OUT. One lost race, every tab on the login screen, with
// a perfectly good RT2 sitting in localStorage.
//
// ----------------------------------------------------------------------------
// WEB LOCKS ONLY  (round-1 review P2-1, Master ruling)
// ----------------------------------------------------------------------------
// This module used to carry a localStorage "lease" fallback for browsers
// without `navigator.locks`. It is DELETED, and deliberately not replaced,
// because a lease cannot do the one job the lock exists to do:
//
//   * It expired on a TIMER (10 s). A refresh slower than the TTL -- a cold
//     Railway container is routinely slower -- let a second tab take the lease
//     while the first was still airborne. Both POST, the loser gets a 401, and
//     the user is logged out: exactly the bug, reintroduced by the mechanism
//     meant to prevent it.
//   * Acquisition was read-then-write with no compare-and-swap, so two tabs
//     that read "free" in the same instant could both write and both believe
//     they had won.
//
// A lock that is unreliable precisely when the network is slow is worse than no
// lock, because it invites the caller to trust it. So:
//
//   * `navigator.locks` present -> a real mutex, released by the browser even
//     if the holding tab is killed mid-rotation.
//   * absent -> SINGLE-TAB MODE. No cross-tab coordination is attempted or
//     claimed. `fn` runs directly, which is the pre-H2 behaviour and correct
//     for the overwhelmingly common case of one tab; with several tabs on such
//     a browser the original race remains, and that is stated rather than
//     papered over. Web Locks ship in Chrome 69+, Edge 79+, Firefox 96+ and
//     Safari 15.4+, so this is a narrow tail.
//
// AND: after a bounded wait for the lock, we NEVER run an unsynchronised
// rotation. The old fallback did (`LEASE_MAX_WAIT_MS`, then "run anyway"),
// which turned a slow winner into two POSTs and a logout. Waiting out the
// deadline now raises RefreshLockTimeoutError; the caller surfaces
// "reconnecting" and waits for the winner's published pair, because a tab that
// cannot get the lock is a tab whose rotation someone else is already doing.
//
// Deliberately dependency-free (no '@/lib/api' import) for the same reason as
// lib/authTokenSync and lib/authBroadcast: lib/api.ts imports THIS, so the
// arrow may only point one way.
// ============================================================================

/** Name of the cross-tab mutex. */
const LOCK_NAME = 'orkstra-auth-refresh'

/**
 * How long to wait for another tab's rotation before giving up.
 *
 * Generous on purpose: the winner is doing the work, and the right outcome for
 * a waiter is almost always "let it finish, then adopt its token". This
 * deadline exists only so a wedged holder cannot hang the caller's request
 * forever -- and expiring it means "reconnecting", never "rotate anyway".
 *
 * It bounds the WAIT ONLY. It is disarmed the instant the lock is granted, so a
 * rotation may take as long as it takes without its outcome being relabelled as
 * a lock timeout (round-2 review P2 -- see withRefreshLock).
 */
export const LOCK_WAIT_MS = 20_000

/** Raised when the lock could not be obtained inside {@link LOCK_WAIT_MS}. */
export class RefreshLockTimeoutError extends Error {
  constructor() {
    super('REFRESH_LOCK_TIMEOUT')
    this.name = 'RefreshLockTimeoutError'
  }
}

interface LockManagerLike {
  request<T>(
    name: string,
    options: { signal?: AbortSignal },
    fn: () => Promise<T>,
  ): Promise<T>
}

function webLocks(): LockManagerLike | null {
  try {
    const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks
    return typeof locks?.request === 'function' ? locks : null
  } catch {
    // A sandboxed iframe can throw on the property access alone.
    return null
  }
}

/** True when this browser can coordinate rotations between tabs. */
export function hasCrossTabLock(): boolean {
  return webLocks() !== null
}

/**
 * Run `fn` with the cross-tab refresh lock held.
 *
 * Callers must assume the world moved while they waited: the whole point is
 * that another tab may have rotated in the meantime, so re-read the refresh
 * token INSIDE `fn` rather than capturing it before the call.
 *
 * LOCK_WAIT_MS bounds the WAIT and nothing else. Once `fn` is running the
 * deadline is spent: `fn` owns its own timeouts (the refresh POST is bounded by
 * its own AbortController) and its rejection is the caller's to classify.
 *
 * @throws RefreshLockTimeoutError if the lock is not obtained in LOCK_WAIT_MS.
 *         `fn` has NOT run, and must not be run unsynchronised by the caller.
 *         Any OTHER rejection came from `fn` and is propagated unchanged.
 */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = webLocks()

  // SINGLE-TAB MODE. No mutex available; run directly and make no cross-tab
  // promise. See the header: a localStorage lease is not a substitute.
  if (!locks) return fn()

  const controller = new AbortController()
  let timedOut = false
  // ROUND-2 REVIEW P2. The deadline used to stay armed for the whole call, so a
  // rotation that STARTED inside LOCK_WAIT_MS but finished outside it got its
  // real outcome overwritten:
  //
  //   19 s  lock acquired, POST /auth/refresh goes out
  //   20 s  the timer fires anyway -> timedOut = true
  //   21 s  the POST answers 401 -- the refresh token IS dead
  //         -> the catch below saw `timedOut` and reported
  //            RefreshLockTimeoutError, which the caller turns into
  //            "reconnecting" / RefreshStalledError: a TRANSPORT shape.
  //
  // So a server that had explicitly refused the token was reported as a slow
  // network, the session was kept, and the REVOKED refresh token stayed in
  // localStorage -- a session that only looks alive, which is worse than being
  // signed out, because every later request fails and nothing ever re-logs-in.
  //
  // `acquired` is the authority, not the clock: the timer is disarmed the moment
  // the lock callback starts, and the catch only manufactures a timeout error
  // when the lock was never obtained at all. Both are needed -- a timer can fire
  // in the gap between the browser granting the lock and invoking the callback,
  // and after a grant `abort()` is a no-op, so `timedOut` alone can be true for
  // a call that did run.
  let acquired = false
  const timer = setTimeout(() => {
    if (acquired) return
    timedOut = true
    controller.abort()
  }, LOCK_WAIT_MS)

  try {
    // `signal` makes the browser stop WAITING for the lock; it never interrupts
    // `fn` once the lock is held, so an abort can only mean "still queued" --
    // and once we are past the grant, no abort should be raised at all.
    return await locks.request(LOCK_NAME, { signal: controller.signal }, () => {
      acquired = true
      clearTimeout(timer)
      return fn()
    })
  } catch (error) {
    // Only a lock we never got is a lock timeout. Once `fn` has started, its
    // rejection is the truth -- a 401 must still end the session.
    if (timedOut && !acquired) throw new RefreshLockTimeoutError()
    // A rejection from `fn` is the caller's business -- re-throw it untouched.
    // There is no fallback path to divert to any more, which is the point:
    // running `fn` a second time is the double-POST this module exists to
    // prevent.
    throw error
  } finally {
    clearTimeout(timer)
  }
}

/** Test-only. Nothing is held outside a `withRefreshLock` call any more. */
export function __resetRefreshLockForTests(): void {
  /* no persistent state to reset: the Web Locks API owns the lock */
}
